import React, { useState, useCallback } from 'react';
import { Accordion, AccordionTitle, AccordionContent, Link, SkeletonLoader, Typography } from '@hpi-sox/hpstellar-core';

import RadioLink from './radio-option-link';
import useTemplateTheme from '../../../../../hooks/useTemplateTheme';
import useDeviceInfo from '../../../../../hooks/useDeviceInfo';
import { Helpers } from '../../../../../core/src/helpers';

import './radio-option-group.less';

const DEFAULT_TRANSLATION = {
    seeMoreLink: 'See More Specs',
    seeLessLink: 'See Less Specs',
};

const OptionsList = ({
    options,
    maxLength,
    isDesktop,
    isLoading,
    defaultUnavalableTooltip,
    onRadioClick,
    content,
    disclaimer,
}) => {
    const onClick = useCallback(
        option => () => {
            onRadioClick(option);
        },
        [options],
    );
    return (
        <>
            {disclaimer && <div className="cto-options-config-label">{disclaimer}</div>}
            {options.map((option, idx) => {
                const { title, value = [], tooltip: description } = option;
                if (idx > maxLength - 1) {
                    return null;
                }
                const [selectedOption] = value.filter(o => o.checked);
                const { label: selectedLabel } = selectedOption || {};

                return (
                    <Accordion
                        key={title}
                        size="small"
                        isStatic={isDesktop}
                        defaultExpanded={true}
                        enableScrollIntoView={true}
                        iconType={'caret'}
                    >
                        <AccordionTitle className="radio-option-title" tag="span">
                            <div>
                                <div className="accordion-title-row">
                                    <Typography tag="span" variant="bodyS">
                                        {title}
                                    </Typography>
                                    {description && (
                                        <span
                                            className="group-description"
                                            dangerouslySetInnerHTML={Helpers.createMarkup(description)}
                                        />
                                    )}
                                </div>
                                {selectedLabel && !isDesktop && (
                                    <small className="radio-option-subtitle">{selectedLabel}</small>
                                )}
                            </div>
                        </AccordionTitle>
                        <AccordionContent
                            className="radio-option-content"
                            activeClassName="radio-option-content-expanded"
                        >
                            <div className="radio-option-flex">
                                {value.map((value, idx) => {
                                    const { unavalableTooltip = defaultUnavalableTooltip, ...rest } = value;
                                    return isLoading ? (
                                        <SkeletonLoader key={idx} className="radio-option-loader" />
                                    ) : (
                                        <RadioLink
                                            key={idx}
                                            className="radio-option"
                                            {...rest}
                                            unavalableTooltip={isDesktop && unavalableTooltip}
                                            onRadioClick={onClick(value)}
                                            groupName={title}
                                            tooltipClassName="pdp-cto-radio-option-tooltip"
                                        />
                                    );
                                })}
                            </div>
                            {!isDesktop && content && content({ isDesktop, groupIdx: idx })}
                        </AccordionContent>
                    </Accordion>
                );
            })}
        </>
    );
};

OptionsList.whyDidYouRender = true;

const RadioOptionGroup = ({
    options,
    maxOptions = 5,
    translations = DEFAULT_TRANSLATION,
    defaultUnavalableTooltip,
    isLoading,
    content,
    disclaimer,
    onRadioClick = () => {},
}) => {
    const { typography } = useTemplateTheme(['typography']);
    const { primaryButtonLinkClassName } = typography || {};
    const { device } = useDeviceInfo(['device']);
    const isDesktop = device !== 'mobile';
    const [maxLength, setMaxLength] = useState(() => maxOptions);
    const { seeLessLink, seeMoreLink } = translations;
    const onSeeMoreClick = useCallback(() => {
        if (options.length > maxLength) {
            setMaxLength(options.length);
        } else {
            setMaxLength(maxOptions);
        }
    }, [maxLength, maxOptions, options]);

    if (!options) {
        return null;
    }

    return (
        <div className="radio-option-group">
            <OptionsList
                maxLength={maxLength}
                options={options}
                isDesktop={isDesktop}
                defaultUnavalableTooltip={defaultUnavalableTooltip}
                isLoading={isLoading}
                onRadioClick={onRadioClick}
                content={content}
                disclaimer={disclaimer}
            />
            {options.length > maxOptions && (
                <Link
                    icon="angle"
                    onClick={onSeeMoreClick}
                    className={`radio-option-toggle${primaryButtonLinkClassName ? ` ${primaryButtonLinkClassName}` : ''}`}
                >
                    {options.length > maxLength ? seeMoreLink : seeLessLink}
                </Link>
            )}
        </div>
    );
};

RadioOptionGroup.whyDidYouRender = true;

export default RadioOptionGroup;
